import { 
  Component, 
  Input,
  Output,
  EventEmitter,
  forwardRef, 
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { CommonModule } from '@angular/common';


import { AsyncPipe, JsonPipe } from '@angular/common';
import { concat, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap, debounceTime, map} from 'rxjs/operators';
import { FormsModule } from '@angular/forms';

import { NgSelectModule } from '@ng-select/ng-select';
import { MatIconModule } from '@angular/material/icon';
import { HighlightPipe } from '../../Utils/highlight.pipe';

@Component({
  selector: 'app-autocomplete-search',
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    MatIconModule,
    HighlightPipe
  ],
  standalone: true,
  templateUrl: './autocomplete-search.component.html',
  styleUrl: './autocomplete-search.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteSearchComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutocompleteSearchComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder = 'Search...';
  @Input() bindLabel: string = 'label';
  @Input() bindValue: string = 'value';
  @Input() multiple: boolean = false;
  @Input() fetchItemsFn!: (term: string) => Observable<any[]>;
  @Input() clearable: boolean = true;

  @Output() selectionChanged = new EventEmitter<any>();

  items: any[] = [];
  isLoading = false;
  searchTerm$ = new Subject<string>();
  subscription!: Subscription;

  selectedValue: any;
  private onChange = (_: any) => {};
  private onTouched = () => {};

  value: any;
  constructor(private cdr: ChangeDetectorRef) {}
  
  ngOnInit() {
    this.subscription = this.searchTerm$
    .pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => (this.isLoading = true)),
      switchMap((term: string) => {
        if (!term || term.trim().length < 2) {
          this.isLoading = false;
          return of([]);
        }
        return from(this.fetchItemsFn(term.trim())).pipe(
          catchError(() => {
            this.isLoading = false;
            return of([]);
          }),
          tap(() => {
            this.isLoading = false;
          })
        );
      }
      )
    )
    .subscribe(results => {
      this.items = results;
      this.cdr.detectChanges();
    });
  }

  onSearch(term: string) {
    if (term.length >= 3) {
      this.searchTerm$.next(term);
    } else {
      this.items = [];
    }
  }

  onValueChange(selection: any) {
    this.isLoading = false;
    this.selectedValue = selection;

    let emittedValue: any = selection;
    if (this.bindValue && selection && typeof selection === 'object') {
      emittedValue = selection[this.bindValue];
    }

    this.onChange(emittedValue);
    this.onTouched();
    this.selectionChanged.emit(emittedValue);
  }

  writeValue(value: any): void {
    this.value = value;
    if (this.bindValue && value != null && this.items && Array.isArray(this.items)) {
      const matchedItem = this.items.find(item => item[this.bindValue] === value);
      if (matchedItem) {
        this.selectedValue = matchedItem;
      }
    } else {
      this.selectedValue = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

}
